import React from "react";
import { motion } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

// Fade + rise reveal on scroll
export const Reveal = ({ children, delay = 0, y = 40, className = "", once = true }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once, amount: 0.25 }}
    transition={{ duration: 1, ease: EASE, delay }}
  >
    {children}
  </motion.div>
);

// Masked line reveal — used for signature headline moments.
// trigger="load" plays on mount (reliable for above-the-fold hero);
// trigger="view" plays when scrolled into view.
export const MaskLine = ({ children, delay = 0, className = "", duration = 1.1, trigger = "view" }) => {
  const anim =
    trigger === "load"
      ? { animate: { y: "0%" } }
      : { whileInView: { y: "0%" }, viewport: { once: true, amount: 0.35 } };
  return (
    <span className="block overflow-hidden">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "110%" }}
        {...anim}
        transition={{ duration, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
};

// Word-by-word masked reveal for large headlines
export const MaskWords = ({ text, className = "", stagger = 0.06, delay = 0, trigger = "view" }) => {
  const words = text.split(" ");
  const anim = (i) =>
    trigger === "load"
      ? { animate: { y: "0%" } }
      : { whileInView: { y: "0%" }, viewport: { once: true, amount: 0.3 } };
  return (
    <span className={className}>
      {words.map((w, i) => (
        <span key={i} className="inline-block overflow-hidden align-bottom">
          <motion.span
            className="inline-block"
            initial={{ y: "110%" }}
            {...anim(i)}
            transition={{ duration: 1, ease: EASE, delay: delay + i * stagger }}
          >
            {w}
            {i < words.length - 1 ? "\u00A0" : ""}
          </motion.span>
        </span>
      ))}
    </span>
  );
};

export const Overline = ({ children, className = "" }) => (
  <span className={`overline inline-flex items-center gap-3 text-base ${className}`}>
    <span className="inline-block h-px w-8 bg-current opacity-50" />
    {children}
  </span>
);

// Luxury button with slow fill
export const LuxButton = ({
  children,
  onClick,
  variant = "solid",
  className = "",
  testid,
  type = "button",
}) => {
  const base =
    "group relative inline-flex items-center justify-center overflow-hidden px-9 py-4 text-[0.78rem] uppercase tracking-[0.22em] font-medium transition-colors duration-500";
  const styles =
    variant === "solid"
      ? "bg-forest text-warm"
      : variant === "light"
      ? "bg-warm text-ink"
      : "border border-current text-current bg-transparent";
  return (
    <button
      type={type}
      onClick={onClick}
      data-testid={testid}
      className={`${base} ${styles} ${className}`}
    >
      <span className="relative z-10 transition-transform duration-500 group-hover:-translate-y-[1px]">
        {children}
      </span>
      <span
        className={`absolute inset-0 z-0 origin-bottom scale-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-y-100 ${
          variant === "solid" ? "bg-copper" : variant === "light" ? "bg-champagne" : "bg-current"
        }`}
      />
    </button>
  );
};
